import React, { useState, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import { motion, useReducedMotion } from "framer-motion";
import api from "@/lib/api";
import { openCookiePreferences } from "@/lib/cookieConsent";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import { useConfirm } from "@/components/ui/confirm";
import { Icon } from "@/components/Icon";

/* PRYMEBET redesign — Account (design "06 Account / AC-03"). Left "On this page"
   rail (profile + section nav) beside the settings content. Wired to real
   data/endpoints where they exist; features the backend does not yet expose
   (self-exclusion, stake limits, saved preferences) are honest placeholders. */

const SECTIONS = [
  { id: "profile", label: "Profile" },
  { id: "security", label: "Security" },
  { id: "verification", label: "Verification" },
  { id: "responsible", label: "Responsible play" },
  { id: "preferences", label: "Preferences" },
  { id: "legal", label: "Legal" },
  { id: "support", label: "Support" },
];

function Row({ label, hint, children }) {
  return (
    <div className="flex flex-col gap-3 border-b border-surface-border py-4 last:border-0 sm:flex-row sm:items-center sm:justify-between">
      <div>
        <div className="text-sm font-semibold text-white">{label}</div>
        {hint ? <div className="mt-0.5 text-xs text-meta">{hint}</div> : null}
      </div>
      <div className="shrink-0">{children}</div>
    </div>
  );
}

// §7 one-shot staggered entrance (reduced-motion guarded by the caller — static fallback).
const SECTION_STAGGER = { show: { transition: { staggerChildren: 0.06, delayChildren: 0.04 } } };
const SECTION_ITEM = {
  hidden: { opacity: 0, y: 16 },
  show: { opacity: 1, y: 0, transition: { duration: 0.3, ease: [0.22, 1, 0.36, 1] } },
};

function Card({ id, title, eyebrow, children, variants }) {
  return (
    <motion.section variants={variants} id={id} className="surface-premium scroll-mt-24 rounded-2xl p-5">
      {eyebrow ? <p className="text-[10px] font-extrabold uppercase tracking-[0.2em] text-pnr-green">{eyebrow}</p> : null}
      <h2 className="mb-2 font-display text-lg font-black tracking-tight text-white sm:text-xl">{title}</h2>
      <div>{children}</div>
    </motion.section>
  );
}

// Shared green CTA — primary action (PREMIUM_UI_DIRECTION §3). One per section.
const CTA =
  "rounded-full bg-pnr-green px-5 py-2.5 text-sm font-bold text-surface-1 transition-colors hover:bg-pnr-green-hover focus-ring disabled:cursor-not-allowed disabled:bg-pnr-green/40 disabled:text-surface-1/60";
// Neutral secondary action.
const NEUTRAL =
  "rounded-full border border-surface-border bg-surface-5 px-5 py-2.5 text-sm font-bold text-white transition-colors hover:bg-surface-4 hover:border-border-strong focus-ring";

export default function Account() {
  const { user, logout, refresh } = useAuth();
  const { confirm } = useConfirm();
  const reduceMotion = useReducedMotion();
  const [editingName, setEditingName] = useState(false);
  const [nameInput, setNameInput] = useState("");
  const [savingName, setSavingName] = useState(false);
  const saveUsername = async () => {
    const next = nameInput.trim();
    if (next === (user?.username || "")) { setEditingName(false); return; }
    setSavingName(true);
    try {
      await api.post("/account/username", { username: next });
      await refresh();
      toast.success("Username updated");
      setEditingName(false);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not update username");
    } finally { setSavingName(false); }
  };
  const navigate = useNavigate();
  const [sendingReset, setSendingReset] = useState(false);
  const [odds, setOdds] = useState(() => localStorage.getItem("pb_odds_format") || "decimal");
  const [notify, setNotify] = useState(() => localStorage.getItem("pb_notify") !== "off");
  const [active, setActive] = useState("profile");

  // scroll-spy: highlight the section currently in view
  useEffect(() => {
    const obs = new IntersectionObserver(
      (entries) => entries.forEach((e) => { if (e.isIntersecting) setActive(e.target.id); }),
      { rootMargin: "-30% 0px -60% 0px" }
    );
    SECTIONS.forEach((s) => { const el = document.getElementById(s.id); if (el) obs.observe(el); });
    return () => obs.disconnect();
  }, []);

  const scrollTo = (id) => { document.getElementById(id)?.scrollIntoView({ behavior: "smooth", block: "start" }); setActive(id); };

  // Identity verification (DeepIDV) — dimension-aware entry point
  const [verif, setVerif] = useState(null);
  const [verifBusy, setVerifBusy] = useState(false);
  useEffect(() => { api.get("/kyc/verification").then(({ data }) => setVerif(data)).catch(() => {}); }, []);
  const startVerify = async () => {
    setVerifBusy(true);
    try {
      const { data } = await api.post("/kyc/deepidv/start", {});
      if (data?.session_url) { window.location.href = data.session_url; return; }
      toast.error("Could not start identity verification. Please try again.");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not start identity verification.");
    } finally { setVerifBusy(false); }
  };

  // Responsible gaming: cooling-off + self-exclusion
  const [rg, setRg] = useState(null);
  const [rgBusy, setRgBusy] = useState(false);
  const RG_LABEL = { "1_day": "1 day", "7_days": "7 days", "30_days": "30 days", "6_months": "6 months", "1_year": "1 year", "3_years": "3 years", "5_years": "5 years", permanent: "Permanent" };
  useEffect(() => { api.get("/account/responsible-gaming").then(({ data }) => setRg(data)).catch(() => {}); }, []);
  const refreshRg = async () => { try { const { data } = await api.get("/account/responsible-gaming"); setRg(data); } catch { /* noop */ } };
  const applyExclusion = async (mode, duration) => {
    const human = RG_LABEL[duration] || duration;
    const msg = mode === "cooloff"
      ? `Take a cooling-off break for ${human}? Purchases and play will be paused until it ends, then resume automatically.`
      : duration === "permanent"
        ? "Permanently self-exclude? This blocks purchases, play, and promotions and CANNOT be reopened. Eligible redemptions and Customer Support remain available."
        : `Self-exclude for ${human}? This is a fixed period that cannot be shortened; reactivation afterward requires a request. Eligible redemptions and Customer Support remain available.`;
    const ok = await confirm({
      title: mode === "cooloff" ? "Take a cooling-off break" : "Confirm self-exclusion",
      body: msg,
      confirmText: mode === "cooloff" ? "Start break" : "Self-exclude",
      danger: mode !== "cooloff",
    });
    if (!ok) return;
    setRgBusy(true);
    try {
      await api.post("/account/responsible-gaming/exclude", { mode, duration });
      toast.success(mode === "cooloff" ? "Cooling-off break applied." : "Self-exclusion applied.");
      await refreshRg(); await refresh();
    } catch (e) { toast.error(e.response?.data?.detail || "Could not apply that."); }
    finally { setRgBusy(false); }
  };
  const reactivate = async () => {
    const ok = await confirm({
      title: "Reactivate your account",
      body: "Reactivate your account? You'll be able to purchase and play again.",
      confirmText: "Reactivate",
    });
    if (!ok) return;
    setRgBusy(true);
    try {
      await api.post("/account/responsible-gaming/reactivate");
      toast.success("Account reactivated.");
      await refreshRg(); await refresh();
    } catch (e) { toast.error(e.response?.data?.detail || "Could not reactivate."); }
    finally { setRgBusy(false); }
  };
  // Deposit limits + session reminder (UIA-22) — backed by GET/POST /me/limits. Tightening a
  // limit applies now; loosening it is staged behind a cooling-off window the backend enforces.
  const [limits, setLimits] = useState(null);
  const [limitDaily, setLimitDaily] = useState("");
  const [limitWeekly, setLimitWeekly] = useState("");
  const [sessionMins, setSessionMins] = useState(0);
  const [limitsBusy, setLimitsBusy] = useState(false);
  const applyLimits = (data) => {
    setLimits(data);
    setLimitDaily(data?.daily != null ? String(data.daily) : "");
    setLimitWeekly(data?.weekly != null ? String(data.weekly) : "");
    setSessionMins(Number(data?.session_reminder_minutes || 0));
  };
  useEffect(() => { api.get("/me/limits").then(({ data }) => applyLimits(data)).catch(() => {}); }, []);
  const saveLimits = async () => {
    const parse = (v) => { const s = String(v).trim(); if (s === "") return null; const n = parseFloat(s); return Number.isFinite(n) && n >= 0 ? n : null; };
    const payload = { daily: parse(limitDaily), weekly: parse(limitWeekly), session_reminder_minutes: Number(sessionMins) || 0 };
    setLimitsBusy(true);
    try {
      const { data } = await api.post("/me/limits", payload);
      applyLimits(data);
      toast.success(data?.pending ? "Saved. Increases take effect after the cooling-off window." : "Deposit limits saved.");
    } catch (e) { toast.error(e.response?.data?.detail || "Could not save your limits."); }
    finally { setLimitsBusy(false); }
  };

  const rgMessage = (b) => {
    if (!b) return "";
    if (b.permanent) return "Your account is permanently self-excluded. Purchases, play, and promotions are closed. Eligible redemptions and Customer Support remain available.";
    if (b.reactivation_required) return "Your self-exclusion period has ended. Reactivate your account to purchase or play again.";
    const until = b.until ? new Date(b.until).toLocaleDateString(undefined, { year: "numeric", month: "short", day: "numeric" }) : "";
    return b.mode === "cooloff"
      ? `You're on a cooling-off break until ${until}. Purchases and play are paused until then.`
      : `Your account is self-excluded until ${until}. Purchases, play, and promotions are blocked until then. Eligible redemptions and Customer Support remain available.`;
  };

  const memberSince = (() => {
    try {
      const d = new Date(user?.created_at);
      if (isNaN(d)) return null;
      return d.toLocaleDateString(undefined, { month: "short", year: "numeric" });
    } catch { return null; }
  })();

  const sendReset = async () => {
    if (!user?.email) return;
    setSendingReset(true);
    try {
      await api.post("/auth/forgot-password", { email: user.email });
      toast.success("Password reset link sent to your email.");
    } catch {
      toast.success("If that email exists, a reset link is on its way.");
    } finally { setSendingReset(false); }
  };

  const setOddsFmt = (v) => { setOdds(v); localStorage.setItem("pb_odds_format", v); toast.success("Odds format saved on this device."); };
  const toggleNotify = () => { const n = !notify; setNotify(n); localStorage.setItem("pb_notify", n ? "on" : "off"); };

  const initials = (user?.username || "PN").slice(0, 2).toUpperCase();
  const isAdmin = user?.role === "admin" || user?.role === "owner" || user?.role === "super_admin";

  return (
    <div className="mx-auto max-w-6xl px-4 py-8 sm:px-6">
      <div className="pt-1">
        <p className="text-[10px] font-extrabold uppercase tracking-[0.2em] text-pnr-green">Account</p>
        <h1 className="mt-1 font-display text-3xl font-black tracking-tight text-white sm:text-4xl">Your account</h1>
      </div>

      <div className="mt-6 grid grid-cols-1 gap-6 lg:grid-cols-12">
        {/* Left rail — profile + "On this page" nav */}
        <aside className="lg:col-span-4">
          <div className="space-y-4 lg:sticky lg:top-24">
            <div className="surface-premium rounded-2xl p-5 text-center">
              <div className="mx-auto flex h-16 w-16 items-center justify-center rounded-2xl bg-surface-5 font-display text-2xl font-extrabold text-meta-strong">
                {initials}
              </div>
              <h2 className="mt-3 font-display text-xl font-black tracking-tight text-white">{user?.username}</h2>
              <p className="truncate text-sm text-meta">{user?.email}</p>
              {memberSince ? <p className="mt-0.5 text-xs text-[#71717a]">Member since {memberSince}</p> : null}
              {user?.vip ? (
                <span className="mt-2 inline-block rounded-full bg-white/[0.06] px-3 py-0.5 text-[11px] font-bold text-meta-strong">
                  {user.vip.tier} · Lvl {user.vip.level}
                </span>
              ) : null}
              {isAdmin && (
                <Link to="/admin" className="mt-3 block rounded-full bg-pnr-green/15 px-3 py-1.5 text-[12px] font-bold text-pnr-green transition-colors hover:bg-pnr-green/25 focus-ring">Admin access →</Link>
              )}
              <button
                data-testid="account-signout"
                onClick={() => { logout(); navigate("/"); }}
                className="mt-3 w-full rounded-full border border-surface-border bg-surface-5 px-5 py-2.5 text-sm font-bold text-white transition-colors hover:bg-surface-4 hover:border-border-strong focus-ring"
              >
                Sign out
              </button>
            </div>

            <nav className="surface-premium rounded-2xl p-2" data-testid="account-nav">
              <p className="px-3 pb-1 pt-2 text-[10px] font-bold uppercase tracking-[0.16em] text-[#71717a]">On this page</p>
              {SECTIONS.map((s) => (
                <button
                  key={s.id}
                  data-testid={`account-nav-${s.id}`}
                  onClick={() => scrollTo(s.id)}
                  className={`block w-full rounded-lg px-3 py-2 text-left text-sm font-semibold transition-colors focus-ring ${active === s.id ? "bg-surface-5 text-white" : "text-meta hover:text-white"}`}
                >
                  {s.label}
                </button>
              ))}
            </nav>
          </div>
        </aside>

        {/* Content — §7 one-shot staggered entrance; each Card is a motion item.
            Reduced motion: static, no stagger. */}
        <motion.div
          className="space-y-6 lg:col-span-8"
          variants={reduceMotion ? undefined : SECTION_STAGGER}
          initial={reduceMotion ? false : "hidden"}
          animate="show"
        >
          {/* Profile */}
          <Card id="profile" eyebrow="Profile" title="Account details" variants={reduceMotion ? undefined : SECTION_ITEM}>
            <Row label="Username" hint="Your public display name — change it any time">
              {editingName ? (
                <div className="flex items-center gap-2">
                  <input
                    data-testid="account-username-input"
                    value={nameInput}
                    onChange={(e) => setNameInput(e.target.value)}
                    maxLength={20}
                    autoFocus
                    autoComplete="username"
                    onKeyDown={(e) => { if (e.key === "Enter") saveUsername(); if (e.key === "Escape") setEditingName(false); }}
                    className="h-9 w-44 rounded-lg border border-surface-border bg-surface-5 px-3 text-sm text-white outline-none transition-colors focus:border-pnr-green focus-ring"
                  />
                  <button data-testid="account-username-save" disabled={savingName} onClick={saveUsername} className="h-9 rounded-full bg-pnr-green px-4 text-sm font-bold text-surface-1 transition-colors hover:bg-pnr-green-hover focus-ring disabled:cursor-not-allowed disabled:bg-pnr-green/40 disabled:text-surface-1/60">{savingName ? "…" : "Save"}</button>
                  <button disabled={savingName} onClick={() => setEditingName(false)} className="h-9 rounded-full border border-surface-border px-3 text-sm font-semibold text-meta transition-colors hover:text-white hover:border-border-strong focus-ring">Cancel</button>
                </div>
              ) : (
                <div className="flex items-center gap-3">
                  <span className="text-sm font-semibold text-white">{user?.username}</span>
                  <button data-testid="account-username-edit" onClick={() => { setNameInput(user?.username || ""); setEditingName(true); }} className="rounded-full border border-surface-border px-3 py-1 text-xs font-bold text-meta transition-colors hover:text-white hover:border-border-strong focus-ring">Edit</button>
                </div>
              )}
            </Row>
            <Row label="Email">
              <span className="text-sm text-meta">{user?.email}</span>
            </Row>
            <Row label="Member since">
              <span className="text-sm text-meta">{memberSince || "—"}</span>
            </Row>
          </Card>

          {/* Security */}
          <Card id="security" eyebrow="Security" title="Password" variants={reduceMotion ? undefined : SECTION_ITEM}>
            <Row label="Reset your password" hint="We'll email a secure link to set a new password.">
              <button
                data-testid="account-send-reset"
                onClick={sendReset}
                disabled={sendingReset}
                className={CTA}
              >
                {sendingReset ? "Sending…" : "Send reset link"}
              </button>
            </Row>
          </Card>

          {/* Verification */}
          <Card id="verification" eyebrow="Verification" title="Identity (KYC)" variants={reduceMotion ? undefined : SECTION_ITEM}>
            {(() => {
              const idStatus = verif?.identity_status || (user?.kyc_status === "approved" ? "approved" : "not_started");
              const badge = {
                approved: ["Verified", "text-pnr-green bg-pnr-green/15"],
                in_progress: ["In progress", "text-[#eab308] bg-[#eab308]/15"],
                pending_review: ["Under review", "text-[#eab308] bg-[#eab308]/15"],
                expired: ["Expired", "text-pnr-red bg-pnr-red/15"],
                rejected: ["Action needed", "text-pnr-red bg-pnr-red/15"],
                not_started: ["Not started", "text-meta bg-surface-5"],
              }[idStatus] || ["Not started", "text-meta bg-surface-5"];
              const canStart = ["not_started", "rejected", "expired"].includes(idStatus);
              return (
                <>
                  <Row label="Identity verification"
                       hint="A one-time ID + selfie check. Unlocks PCash play and prize redemptions.">
                    <span className={`rounded-full px-3 py-1 text-xs font-bold ${badge[1]}`} data-testid="acct-kyc-badge">{badge[0]}</span>
                  </Row>
                  {verif?.compliance_hold && (
                    <div className="mt-3 rounded-xl border border-[#854d0e] bg-[#1c1507] p-3 text-xs text-[#eab308]">
                      Your account is under compliance review{verif?.compliance_hold_reason ? `: ${verif.compliance_hold_reason}` : ""}. Our team will follow up — contact Support if you have questions.
                    </div>
                  )}
                  {idStatus === "approved" ? (
                    <p className="mt-3 text-sm text-meta">You're verified. ✅ PCash play and redemptions are unlocked.</p>
                  ) : (idStatus === "in_progress" || idStatus === "pending_review") ? (
                    <p className="mt-3 text-sm text-meta">Your verification is being reviewed. We'll email you as soon as it's complete.</p>
                  ) : (
                    <div className="mt-3">
                      {idStatus === "rejected" && (
                        <p className="mb-2 text-sm text-pnr-red">Your last attempt wasn't approved{user?.kyc_reject_reason ? `: ${user.kyc_reject_reason}` : "."} You can try again below.</p>
                      )}
                      {idStatus === "expired" && (
                        <p className="mb-2 text-sm text-meta">Your verification has expired. Please re-verify to continue.</p>
                      )}
                      {verif?.deepidv_enabled === false ? (
                        <Link to="/wallet" className={`inline-flex items-center gap-1.5 ${NEUTRAL}`}>
                          Verify in Wallet <Icon name="arrow-right" size={14} />
                        </Link>
                      ) : (
                        <button data-testid="acct-start-verify" onClick={startVerify} disabled={verifBusy || !canStart}
                          className={CTA}>
                          {verifBusy ? "Starting…" : "Verify my identity"}
                        </button>
                      )}
                    </div>
                  )}
                  <p className="mt-3 text-[11px] text-[#71717a]">
                    We use third-party identity verification providers under our{" "}
                    <Link to="/privacy" className="text-pnr-green underline underline-offset-2 hover:text-white focus-ring">Privacy Policy</Link>; where biometric data is involved the{" "}
                    <Link to="/biometric-notice" className="text-pnr-green underline underline-offset-2 hover:text-white focus-ring">Biometric Notice</Link> applies before capture.
                  </p>
                </>
              );
            })()}
          </Card>

          {/* Responsible Play — real controls wired to /account/responsible-gaming + /me/limits */}
          <Card id="responsible" eyebrow="Responsible play" title="Limits & self-exclusion" variants={reduceMotion ? undefined : SECTION_ITEM}>
            <div className="mb-3 flex items-start gap-2 rounded-xl border border-surface-border bg-surface-5 p-3">
              <Icon name="shield-alert" size={16} className="mt-0.5 shrink-0 text-meta" />
              <p className="text-xs leading-relaxed text-meta">
                Play for entertainment. If play becomes hard to control, take a cooling-off break or self-exclude. During any
                restriction, purchases, play, and promotions are blocked and marketing stops — eligible redemptions and Customer
                Support stay available. See the <Link to="/responsible-play" className="text-pnr-green underline underline-offset-2 hover:text-white focus-ring">Responsible Play policy</Link>.
              </p>
            </div>

            {rg?.active ? (
              <div className="rounded-xl border border-[#854d0e] bg-[#1c1507] p-4" data-testid="rg-active">
                <p className="text-sm leading-relaxed text-[#eab308]">{rgMessage(rg.block)}</p>
                {rg.block?.reactivation_required && !rg.block?.permanent && (
                  <button data-testid="rg-reactivate" disabled={rgBusy} onClick={reactivate}
                    className="mt-3 rounded-full bg-pnr-green px-5 py-2 text-sm font-bold text-surface-1 transition-colors hover:bg-pnr-green-hover focus-ring disabled:cursor-not-allowed disabled:bg-pnr-green/40 disabled:text-surface-1/60">
                    Reactivate my account
                  </button>
                )}
              </div>
            ) : (
              <>
                <Row label="Take a cooling-off break" hint="A short, temporary pause. Play resumes automatically when it ends.">
                  <div className="flex flex-wrap gap-2" data-testid="rg-cooloff">
                    {["1_day", "7_days", "30_days"].map((d) => (
                      <button key={d} disabled={rgBusy} onClick={() => applyExclusion("cooloff", d)}
                        className="rounded-full border border-surface-border bg-surface-5 px-4 py-2 text-sm font-semibold text-white transition-colors hover:bg-surface-4 hover:border-border-strong focus-ring disabled:cursor-not-allowed disabled:opacity-50">
                        {RG_LABEL[d]}
                      </button>
                    ))}
                  </div>
                </Row>
                <Row label="Self-exclude" hint="A fixed exclusion that cannot be shortened. Permanent cannot be reopened.">
                  <div className="flex flex-wrap gap-2" data-testid="rg-self-exclusion">
                    {["6_months", "1_year", "3_years", "5_years", "permanent"].map((d) => (
                      <button key={d} disabled={rgBusy} onClick={() => applyExclusion("self_exclusion", d)}
                        className={`rounded-full border px-4 py-2 text-sm font-semibold transition-colors focus-ring disabled:cursor-not-allowed disabled:opacity-50 ${d === "permanent" ? "border-pnr-red/50 bg-pnr-red/10 text-pnr-red hover:bg-pnr-red/20" : "border-surface-border bg-surface-5 text-white hover:bg-surface-4 hover:border-border-strong"}`}>
                        {RG_LABEL[d]}
                      </button>
                    ))}
                  </div>
                </Row>
              </>
            )}
            {/* Deposit limits + session reminder (UIA-22) */}
            <div className="mt-5 border-t border-surface-border pt-4" data-testid="account-limits">
              <div className="mb-1 text-sm font-semibold text-white">Deposit limits</div>
              <p className="mb-3 text-xs text-meta">
                Cap how much you can spend. Lowering a limit applies immediately; raising or removing one
                takes effect only after a {limits?.increase_cooldown_hours ? `${limits.increase_cooldown_hours}-hour` : "cooling-off"} wait.
              </p>
              <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
                <label className="block">
                  <span className="text-xs text-meta">Daily limit (USD)</span>
                  <input data-testid="account-limit-daily" value={limitDaily} inputMode="decimal"
                    onChange={(e) => setLimitDaily(e.target.value.replace(/[^0-9.]/g, ""))}
                    placeholder="No limit"
                    className="mt-1 h-10 w-full rounded-lg border border-surface-border bg-surface-5 px-3 text-sm tabular-nums text-white outline-none transition-colors focus:border-pnr-green focus-ring" />
                </label>
                <label className="block">
                  <span className="text-xs text-meta">Weekly limit (USD)</span>
                  <input data-testid="account-limit-weekly" value={limitWeekly} inputMode="decimal"
                    onChange={(e) => setLimitWeekly(e.target.value.replace(/[^0-9.]/g, ""))}
                    placeholder="No limit"
                    className="mt-1 h-10 w-full rounded-lg border border-surface-border bg-surface-5 px-3 text-sm tabular-nums text-white outline-none transition-colors focus:border-pnr-green focus-ring" />
                </label>
              </div>
              <div className="mt-3">
                <span className="text-xs text-meta">Session reminder</span>
                <div className="mt-1 inline-flex flex-wrap gap-1.5" data-testid="account-session-reminder">
                  {[[0, "Off"], [30, "30 min"], [60, "60 min"], [120, "2 hrs"]].map(([v, l]) => (
                    <button key={v} type="button" onClick={() => setSessionMins(v)}
                      className={`rounded-full border px-3.5 py-1.5 text-sm font-semibold transition-colors focus-ring ${Number(sessionMins) === v ? "border-pnr-green bg-pnr-green/10 text-pnr-green shadow-[inset_0_0_0_1px_rgba(0,214,50,0.35)]" : "border-surface-border bg-surface-5 text-meta hover:text-white hover:border-border-strong"}`}>
                      {l}
                    </button>
                  ))}
                </div>
              </div>
              {limits?.pending && (
                <p className="mt-3 rounded-xl border border-[#854d0e] bg-[#1c1507] p-3 text-xs text-[#eab308]" data-testid="account-limit-pending">
                  A limit increase is scheduled and will apply
                  {limits.pending.effective_at ? ` on ${new Date(limits.pending.effective_at).toLocaleString()}` : " after the cooling-off window"}. Your current limit stays in force until then.
                </p>
              )}
              <button data-testid="account-limit-save" onClick={saveLimits} disabled={limitsBusy}
                className={`mt-4 ${CTA}`}>
                {limitsBusy ? "Saving…" : "Save limits"}
              </button>
            </div>

            <div className="mt-3 text-xs text-[#71717a]">
              Problem gambling help: <b className="text-meta">1-800-MY-RESET</b> or{" "}
              <a href="https://www.1800myreset.org" target="_blank" rel="noopener noreferrer" className="text-pnr-green underline underline-offset-2 hover:text-white focus-ring">1800myreset.org</a> — call or text.
            </div>
          </Card>

          {/* Preferences */}
          <Card id="preferences" eyebrow="Preferences" title="Display & notifications" variants={reduceMotion ? undefined : SECTION_ITEM}>
            <Row label="Odds format" hint="How prices are shown across the sportsbook.">
              <div className="inline-flex rounded-full border border-surface-border bg-surface-4 p-1">
                {[["decimal", "Decimal"], ["american", "American"]].map(([v, l]) => (
                  <button
                    key={v}
                    onClick={() => setOddsFmt(v)}
                    className={`rounded-full px-4 py-1.5 text-sm font-semibold transition-colors focus-ring ${odds === v ? "bg-white text-surface-1" : "text-meta hover:text-white"}`}
                  >
                    {l}
                  </button>
                ))}
              </div>
            </Row>
            <Row label="Notifications" hint="Bet settlements, rewards and promotions.">
              <button
                onClick={toggleNotify}
                aria-pressed={notify}
                aria-label="Notifications"
                className={`relative h-7 w-12 rounded-full transition-colors focus-ring ${notify ? "bg-pnr-green" : "bg-surface-5"}`}
              >
                <span className={`absolute top-1 h-5 w-5 rounded-full bg-white transition-all ${notify ? "left-6" : "left-1"}`} />
              </button>
            </Row>
          </Card>

          {/* Legal */}
          <Card id="legal" eyebrow="Legal" title="Terms & policies" variants={reduceMotion ? undefined : SECTION_ITEM}>
            <div className="grid grid-cols-1 gap-2 sm:grid-cols-3">
              {[
                ["Terms of Service", "/terms"],
                ["Sweepstakes Rules", "/sweeps-rules"],
                ["Privacy Policy", "/privacy"],
                ["Responsible Play", "/responsible-play"],
                ["Complaints & Disputes", "/complaints"],
                ["All legal & policies", "/legal"],
              ].map(([l, to]) => (
                <Link key={l} to={to} className="flex items-center justify-between rounded-xl border border-surface-border bg-surface-5 px-4 py-3 text-sm font-semibold text-white transition-colors hover:bg-surface-4 hover:border-border-strong focus-ring">
                  {l} <Icon name="arrow-right" size={14} className="text-[#71717a]" />
                </Link>
              ))}
              <button type="button" onClick={openCookiePreferences} data-testid="account-cookie-prefs" className="flex items-center justify-between rounded-xl border border-surface-border bg-surface-5 px-4 py-3 text-left text-sm font-semibold text-white transition-colors hover:bg-surface-4 hover:border-border-strong focus-ring">
                Cookie Preferences <Icon name="arrow-right" size={14} className="text-[#71717a]" />
              </button>
            </div>
          </Card>

          {/* Support */}
          <Card id="support" eyebrow="Help" title="Support" variants={reduceMotion ? undefined : SECTION_ITEM}>
            <Row label="Contact PRYMEBET support" hint="Questions about your account, redemptions or a bet.">
              <Link to="/support" className={`inline-flex items-center gap-1.5 ${NEUTRAL}`}>
                Ask a question <Icon name="arrow-right" size={14} />
              </Link>
            </Row>
          </Card>
        </motion.div>
      </div>
    </div>
  );
}
